import { SpecsConfig } from "@/components/settings/forms/resources-form/SpecsConfigTypes";
import { permissionGrpcToLiteral, permissionLiteralToGrpc } from "@/gen_src/grpc_permissions";
import { PermissionLiteral } from "@/gen_src/permissions";
import { Permission } from "@/gen_src/proto/chalk/auth/v1/permissions_pb";
import { Environment } from "@/gen_src/proto/chalk/server/v1/environment_pb";
import {
  GetTeamPermissionsResponse,
  GetTeamResponse,
  Project,
  RoleDescription,
  ScimGroup,
  ScimGroupRoleAssignment,
  Team,
  UserRoleAssignment,
  UserTeamPermissions,
} from "@/gen_src/proto/chalk/server/v1/team_pb";
import { isDefined } from "@/utils/function-utils";
import {
  generatePermissionsByEnvironmentByUserIdGrpc,
  getScimGroupsByUserIdGrpc,
  labeledRolesByEnvironmentByUserId,
  rolesByPermission,
  scimRolebindingsByEnvironment,
} from "@/utils/permissions.utils";
import { protoToJson } from "@/utils/proto-utils";
import { Timestamp, ValueSchema } from "@bufbuild/protobuf/wkt";
import { intersection, keyBy, sortBy } from "lodash";

export enum DeploymentStrategy {
  ROLLING = "ROLLING",
  BLUE_GREEN = "BLUE_GREEN",
  FULL_CONTROL = "FULL_CONTROL",
}

export interface TeamUser {
  id: string;
  name?: string;
  email?: string;
  image?: string;
  teamId?: string;
  deactivatedAt?: Timestamp;
}
export interface TeamRole {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}
export type TeamEnvironment = Environment;
export type TeamScimGroup = ScimGroup;
export type TeamScimGroupRoleAssignment = ScimGroupRoleAssignment;

export interface TeamScimGroupWithRoleAssignment extends TeamScimGroup {
  roleAssignment: TeamScimGroupRoleAssignment | null;
}

export function isSpecificProtoPermission(permission: Permission): boolean {
  const permissionName = Permission[permission];
  return (
    permissionName != null &&
    permission !== Permission.UNSPECIFIED &&
    permission !== Permission.INSECURE_UNAUTHENTICATED &&
    permission !== Permission.AUTHENTICATED &&
    permissionName !== "UNKNOWN" &&
    !permissionName.endsWith("_UNKNOWN")
  );
}

export interface TeamService {
  memberByName: (name: string) => TeamUser | undefined;
  memberById: (id: string) => TeamUser | undefined;
  memberByEmail: (email: string) => TeamUser | undefined;
  roleById: (id: string) => TeamRole | undefined;
  environmentById: (environmentId: string) => TeamEnvironment | undefined;
  projectById: (projectId: string) => Project | undefined;
  protoPermissionsForUserEnv: (environmentId: string, userId: string) => Set<Permission>;
  protoPermissionsForUserTeamScoped: (userId: string) => Set<Permission>;
  permissionsForUserEnv: (environmentId: string, userId: string) => Set<PermissionLiteral>;
  permissionsForUserTeamScoped: (userId: string) => Set<PermissionLiteral>;
  allRolesForUserForEnvironment: (environmentId: string, userId: string) => TeamRole[];
  scimRolesForUserForEnvironment: (environmentId: string, userId: string) => TeamRole[];
  individualRoleForUserForEnvironment: (
    environmentId: string,
    userId: string,
  ) => TeamRole | undefined;
  individualRoleForUserTeamScoped(userId: string): TeamRole | undefined;
  scimGroupsForUser: (userId: string) => TeamScimGroup[];
  scimGroups: TeamScimGroup[];
  rolesWithPermissions: (permission: PermissionLiteral[]) => TeamRole[];
  rolesWithPermissionsProto: (permission: Permission[]) => TeamRole[];
  rolesWithAnyPermissionProto: (permissions: Permission[]) => TeamRole[];
  scimRolebindingsByEnvironment: (environmentId: string) => TeamScimGroupRoleAssignment[];
  scimGroupForId: (id: string) => TeamScimGroup | undefined;
  deploymentStrategyForEnvironment: (environmentId: string) => DeploymentStrategy;
  scimGroupsForEnvironment: (environmentId: string) => TeamScimGroupWithRoleAssignment[];
  roles: TeamRole[];
  members: TeamUser[];
  environments: TeamEnvironment[];
  projects: Project[];
  loaded: boolean;
}

export class TeamServiceImpl implements TeamService {
  private membersByName: { [name: string]: TeamUser | undefined };
  private membersById: { [id: string]: TeamUser | undefined };
  private membersByEmail: { [email: string]: TeamUser | undefined };
  private rolesById: { [roleId: string]: TeamRole };
  private environmentsById: { [environmentId: string]: Environment | undefined };
  private projectsById: { [projectId: string]: Project | undefined };
  private permissionsByEnvironmentByUserId: {
    [environmentId: string]: { [userId: string]: Set<Permission> | undefined };
  };
  private scimGroupsByUserId: {
    [userId: string]: ScimGroup[];
  };
  private labeledRolesByEnvironmentByUser: {
    [environmentId: string]: { [userId: string]: UserRoleAssignment[] };
  };
  private rolesByPermission: { [P in Permission]: string[] };
  private scimGroupRoleAssignmentsByEnvironment: {
    [environmentId: string]: TeamScimGroupRoleAssignment[];
  };
  private scimGroupsById: { [id: string]: TeamScimGroup };
  private teamScopedPermissions: UserTeamPermissions[];
  private teamScopedPermissionsByUserId: { [userId: string]: UserTeamPermissions };

  constructor(
    private team: Team,
    private teamPermissions: GetTeamPermissionsResponse,
  ) {
    this.rolesById = keyBy(teamPermissions.roles, (r) => r.id);

    this.membersByName = keyBy(teamPermissions.teamMembers, (m) => m.name || "unknown");
    this.membersById = keyBy(teamPermissions.teamMembers, (m) => m.id);
    this.membersByEmail = keyBy(teamPermissions.teamMembers, (m) => m.email || "unknown");
    this.environmentsById = keyBy(
      team.projects.flatMap((p) => p.environments),
      (e) => e.id,
    );
    this.projectsById = keyBy(team.projects, (p) => p.id);
    this.scimGroupsByUserId = getScimGroupsByUserIdGrpc(teamPermissions);
    this.permissionsByEnvironmentByUserId =
      generatePermissionsByEnvironmentByUserIdGrpc(teamPermissions);
    this.labeledRolesByEnvironmentByUser = labeledRolesByEnvironmentByUserId(teamPermissions);
    this.rolesByPermission = rolesByPermission(teamPermissions);
    this.scimGroupRoleAssignmentsByEnvironment = scimRolebindingsByEnvironment(teamPermissions);
    this.scimGroupsById = keyBy(teamPermissions.scimGroups, (g) => g.id);
    this.teamScopedPermissions = teamPermissions.teamPermissions?.userTeamPermissions || [];
    this.teamScopedPermissionsByUserId = keyBy(this.teamScopedPermissions, (p) => p.userId);
  }

  public roleById(id: string): TeamRole | undefined {
    return this.rolesById[id];
  }

  public memberByName(name: string): TeamUser | undefined {
    return this.membersByName[name];
  }

  public memberById(id: string): TeamUser | undefined {
    return this.membersById[id];
  }

  public memberByEmail(email: string): TeamUser | undefined {
    return this.membersByEmail[email];
  }

  public environmentById(environmentId: string): TeamEnvironment | undefined {
    return this.environmentsById[environmentId];
  }

  public projectById(projectId: string): Project | undefined {
    return this.projectsById[projectId];
  }

  public deploymentStrategyForEnvironment(environmentId: string): DeploymentStrategy {
    const specConfig = this.environmentsById[environmentId]?.specConfigJson;
    if (!specConfig) {
      return DeploymentStrategy.ROLLING;
    }
    return specConfig.deployment_strategy?.kind.case === "stringValue"
      ? ((specConfig.deployment_strategy?.kind.value ||
          DeploymentStrategy.ROLLING) as DeploymentStrategy)
      : DeploymentStrategy.ROLLING;
  }

  public protoPermissionsForUserEnv(environmentId: string, userId: string): Set<Permission> {
    const permissions = this.permissionsByEnvironmentByUserId[environmentId]?.[userId] ?? new Set();
    return new Set(Array.from(permissions).filter(isSpecificProtoPermission));
  }

  public protoPermissionsForUserTeamScoped(userId: string): Set<Permission> {
    const permissions = this.teamScopedPermissionsByUserId[userId]?.userPermissions ?? new Set();
    return new Set(Array.from(permissions).filter(isSpecificProtoPermission));
  }

  public permissionsForUserEnv(environmentId: string, userId: string): Set<PermissionLiteral> {
    return new Set(
      Array.from(this.protoPermissionsForUserEnv(environmentId, userId))
        .map(permissionGrpcToLiteral)
        .filter(
          (p): p is PermissionLiteral =>
            p !== "authenticated" && p !== "unspecified" && p !== "insecure_unauthenticated",
        ),
    );
  }

  public permissionsForUserTeamScoped(userId: string): Set<PermissionLiteral> {
    return new Set(
      Array.from(this.protoPermissionsForUserTeamScoped(userId))
        .map(permissionGrpcToLiteral)
        .filter(
          (p): p is PermissionLiteral =>
            p !== "authenticated" && p !== "unspecified" && p !== "insecure_unauthenticated",
        ),
    );
  }

  public scimRolesForUserForEnvironment(environmentId: string, userId: string): TeamRole[] {
    const roleIdsFun =
      this.labeledRolesByEnvironmentByUser[environmentId]?.[userId]?.filter(
        (r) => r.type === "scim",
      ) ?? [];
    return roleIdsFun.map((r) => r.roleId).map((roleId) => this.rolesById[roleId]);
  }

  public individualRoleForUserForEnvironment(
    environmentId: string,
    userId: string,
  ): TeamRole | undefined {
    const role = this.labeledRolesByEnvironmentByUser[environmentId]?.[userId]?.find(
      (r) => r.type === "individual",
    );
    return role ? this.rolesById[role.roleId] : undefined;
  }

  public individualRoleForUserTeamScoped(userId: string): TeamRole | undefined {
    const role = this.teamScopedPermissionsByUserId[userId]?.userRoles?.find(
      (r) => r.type === "individual",
    );
    return role ? this.rolesById[role.roleId] : undefined;
  }

  public allRolesForUserForEnvironment(environmentId: string, userId: string): TeamRole[] {
    return (
      this.labeledRolesByEnvironmentByUser[environmentId]?.[userId]?.map(
        (r) => this.rolesById[r.roleId],
      ) ?? []
    );
  }

  public scimGroupsForUser(userId: string): TeamScimGroup[] {
    return this.scimGroupsByUserId[userId] ?? [];
  }

  public scimGroupsForEnvironment(environmentId: string): TeamScimGroupWithRoleAssignment[] {
    const scimGroups = Array.from(Object.values(this.scimGroupsById));
    const assignments = this.scimGroupRoleAssignmentsByEnvironment[environmentId] ?? [];
    const assignmentsByGroupId = Object.fromEntries(
      assignments.map((assignment) => [assignment.groupId, assignment]),
    );

    return scimGroups.map((scimGroup) => ({
      ...scimGroup,
      roleAssignment: assignmentsByGroupId[scimGroup.id] ?? null,
    }));
  }

  public scimGroupForId(id: string): TeamScimGroup | undefined {
    return this.scimGroupsById[id];
  }

  public rolesWithPermissions(permissions: PermissionLiteral[]): TeamRole[] {
    const protoPerms = permissions.map(permissionLiteralToGrpc);
    return this.rolesWithPermissionsProto(protoPerms);
  }

  public rolesWithPermissionsProto(permissions: Permission[]): TeamRole[] {
    if (permissions.length === 0) {
      return [];
    } else if (permissions.length === 1) {
      const roleIds = this.rolesByPermission[permissions[0]];
      return roleIds?.map((roleId) => this.rolesById[roleId]) ?? [];
    } else {
      // not that efficient; the longest input list so far is 2 elements though
      const allRoleLists = permissions.map((p) => this.rolesByPermission[p]);
      const roleIntersection = intersection(...allRoleLists);
      return roleIntersection.map((roleId) => this.rolesById[roleId]);
    }
  }

  // Union of `rolesWithPermissionsProto` for each input permission:
  // every role that holds at least one of the listed permissions. Used by the
  // multi-permission endpoint warning path (additional_permissions_list).
  public rolesWithAnyPermissionProto(permissions: Permission[]): TeamRole[] {
    if (permissions.length === 0) {
      return [];
    }
    const seen = new Set<string>();
    const result: TeamRole[] = [];
    for (const p of permissions) {
      const roleIds = this.rolesByPermission[p];
      if (!roleIds) continue;
      for (const roleId of roleIds) {
        if (seen.has(roleId)) continue;
        seen.add(roleId);
        const role = this.rolesById[roleId];
        if (role) result.push(role);
      }
    }
    return result;
  }

  public scimRolebindingsByEnvironment(environmentId: string): TeamScimGroupRoleAssignment[] {
    return this.scimGroupRoleAssignmentsByEnvironment[environmentId] ?? [];
  }

  get roles(): TeamRole[] {
    return this.teamPermissions.roles;
  }

  get scimGroups(): TeamScimGroup[] {
    return this.teamPermissions.scimGroups;
  }

  get members(): TeamUser[] {
    return this.teamPermissions.teamMembers;
  }

  get environments(): TeamEnvironment[] {
    return this.team.projects.flatMap((p) => p.environments);
  }

  get projects(): Project[] {
    return this.team.projects;
  }

  get loaded(): boolean {
    return true;
  }
}

export const TeamServiceNullImpl: TeamService = {
  memberByName: () => undefined,
  memberById: () => undefined,
  memberByEmail: () => undefined,
  roleById: () => undefined,
  environmentById: () => undefined,
  projectById: () => undefined,
  protoPermissionsForUserEnv: () => new Set(),
  protoPermissionsForUserTeamScoped: () => new Set(),
  permissionsForUserEnv: () => new Set(),
  permissionsForUserTeamScoped: () => new Set(),
  allRolesForUserForEnvironment: () => [],
  scimRolesForUserForEnvironment: () => [],
  individualRoleForUserForEnvironment: () => undefined,
  individualRoleForUserTeamScoped: () => undefined,
  scimGroupsForUser: () => [],
  scimGroups: [],
  rolesWithPermissions: () => [],
  rolesWithPermissionsProto: () => [],
  rolesWithAnyPermissionProto: () => [],
  scimRolebindingsByEnvironment: () => [],
  scimGroupForId: () => undefined,
  deploymentStrategyForEnvironment: () => DeploymentStrategy.ROLLING,
  scimGroupsForEnvironment: () => [],
  roles: [],
  members: [],
  environments: [],
  projects: [],
  loaded: false,
};

export const TEAM_OWNER_ROLE_ID = "team_owner";

export const STANDARD_ROLES = new Set<string>([
  "viewer",
  "data_scientist",
  "developer",
  "admin",
  "owner",
  "team_owner",
]);

export const STANDARD_ROLE_ORDER: { [roleId: string]: number } = {
  viewer: 0,
  data_scientist: 1,
  developer: 2,
  admin: 3,
  owner: 4,
  team_owner: 5,
};

export function roleIsStandardChalkRole(role: RoleDescription): boolean {
  return STANDARD_ROLES.has(role.id);
}

// ugh
export const sortRolesByOrder = <T extends RoleDescription | TeamRole>(roles: T[]): T[] => {
  return sortBy(roles, (role) => STANDARD_ROLE_ORDER[role.id] ?? role.permissions.length);
};

export function getActiveDeploymentFromTeamResponse(
  teamResponse: GetTeamResponse,
  environmentId: string,
): string | undefined {
  const environment = teamResponse.team?.projects
    .flatMap((p) => p.environments)
    .find((e) => e.id === environmentId);
  return environment?.activeDeploymentId;
}

export function getSpecsConfigJsonFromEnvironment(
  environment: Environment,
): SpecsConfig | undefined {
  return environment.specConfigJson != null
    ? (Object.fromEntries(
        Object.entries(environment.specConfigJson).map(([k, v]) => [
          k,
          protoToJson(ValueSchema, v),
        ]),
      ) as SpecsConfig)
    : undefined;
}

export function getAllKubeClustersFromEnvironment(environment: Environment): string[] {
  const clusterSet = new Set<string | undefined>();
  clusterSet.add(environment.kubeClusterName);
  clusterSet.add(environment.branchKubeClusterName);
  clusterSet.add(environment.engineKubeClusterName);
  const resourceGroups = getSpecsConfigJsonFromEnvironment(environment)?.resource_groups ?? [];
  resourceGroups.forEach((group) => {
    clusterSet.add(group.kube_context?.kube_cluster_name);
    clusterSet.add(group.kube_context?.branch_kube_cluster_name);
    clusterSet.add(group.kube_context?.engine_kube_cluster_name);
  });
  return Array.from(clusterSet).filter(isDefined);
}
